/* AdminHealthDashboard — real-time status на 5 критични системи
 * + 7-day heatmap history.
 *
 * Endpoints:
 *   GET  /api/health-monitor/status   — текущ snapshot (no email)
 *   GET  /api/health-monitor/history?days=7
 *   POST /api/health-monitor/run-now  — trigger + email
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/theme/ThemeContext";
import { Activity, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Mail, Database, CreditCard, Brain, Send, HardDrive, Loader2 } from "lucide-react";

const SYSTEM_ICONS = {
  "Stripe": CreditCard,
  "SendGrid": Send,
  "MongoDB": Database,
  "Emergent LLM Key": Brain,
  "Disk Space": HardDrive,
};

const STATUS_COLORS = {
  OK:   { bg: "#DCFCE7", fg: "#14532D", border: "#86EFAC", icon: CheckCircle2 },
  WARN: { bg: "#FEF3C7", fg: "#78350F", border: "#FCD34D", icon: AlertTriangle },
  FAIL: { bg: "#FEE2E2", fg: "#7F1D1D", border: "#FCA5A5", icon: XCircle },
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.OK;
  const Icon = c.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: c.bg, color: c.fg }}
      data-testid={`health-status-${status}`}
    >
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
}

function SystemCard({ check, isDark }) {
  const Icon = SYSTEM_ICONS[check.name] || Activity;
  const c = STATUS_COLORS[check.status] || STATUS_COLORS.OK;
  return (
    <Card
      className={`border-l-4 ${isDark ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)]" : ""}`}
      style={{ borderLeftColor: c.border }}
      data-testid={`health-system-${check.name.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg" style={{ background: c.bg }}>
              <Icon className="w-4 h-4" style={{ color: c.fg }} />
            </div>
            <div>
              <div className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{check.name}</div>
            </div>
          </div>
          <StatusBadge status={check.status} />
        </div>
        <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"} leading-relaxed`}>
          {check.message}
        </div>
        {check.details && Object.keys(check.details).length > 0 && (
          <div className={`mt-2 pt-2 border-t ${isDark ? "border-white/10" : "border-gray-100"} flex flex-wrap gap-2`}>
            {Object.entries(check.details).map(([k, v]) => (
              <span key={k} className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${isDark ? "bg-white/5 text-gray-400" : "bg-gray-100 text-gray-600"}`}>
                {k}={String(v)}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HistoryHeatmap({ history, isDark }) {
  // history е сортирана най-нова първа. За heatmap ни трябва chronological
  // ред (стара → нова).
  const days = [...history].reverse();
  // Извличаме всички уникални system имена
  const systems = Array.from(
    new Set(days.flatMap((d) => Object.keys(d.checks_summary || {})))
  );
  if (days.length === 0) {
    return (
      <div className={`text-xs italic ${isDark ? "text-gray-500" : "text-gray-500"} text-center py-6`}>
        Все още няма historical данни. Cron-ът ще попълни данните след първото нощно изпълнение.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="health-history-heatmap">
      <table className="min-w-full text-xs">
        <thead>
          <tr>
            <th className={`text-left px-2 py-1.5 font-semibold ${isDark ? "text-gray-300" : "text-gray-700"}`}>Система</th>
            {days.map((d) => (
              <th key={d.date} className={`px-1 py-1.5 font-medium text-center ${isDark ? "text-gray-400" : "text-gray-500"}`} style={{ minWidth: "60px" }}>
                <div className="text-[10px]">{d.date.slice(5)}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {systems.map((sys) => {
            const Icon = SYSTEM_ICONS[sys] || Activity;
            return (
              <tr key={sys} className={isDark ? "border-t border-white/5" : "border-t border-gray-100"}>
                <td className={`px-2 py-1.5 font-medium ${isDark ? "text-gray-300" : "text-gray-700"} flex items-center gap-1.5`}>
                  <Icon className="w-3 h-3 opacity-60" /> {sys}
                </td>
                {days.map((d) => {
                  const st = d.checks_summary?.[sys] || "OK";
                  const c = STATUS_COLORS[st];
                  return (
                    <td key={d.date} className="px-1 py-1.5 text-center">
                      <div
                        className="w-7 h-7 rounded-md mx-auto flex items-center justify-center text-[10px] font-bold cursor-help"
                        style={{ background: c.bg, color: c.fg }}
                        title={`${d.date} · ${sys}: ${st}`}
                      >
                        {st[0]}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
          <tr className={isDark ? "border-t border-white/10 font-semibold" : "border-t border-gray-200 font-semibold"}>
            <td className={`px-2 py-1.5 ${isDark ? "text-white" : "text-gray-900"}`}>Общо</td>
            {days.map((d) => {
              const c = STATUS_COLORS[d.overall_status] || STATUS_COLORS.OK;
              return (
                <td key={d.date} className="px-1 py-1.5 text-center">
                  <div
                    className="w-7 h-7 rounded-md mx-auto flex items-center justify-center text-[10px] font-bold"
                    style={{ background: c.bg, color: c.fg }}
                    title={`${d.date} · runs: ${d.runs}`}
                  >
                    {d.overall_status[0]}
                  </div>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function AdminHealthDashboard({ API }) {
  const { isDark } = useTheme();
  const [snapshot, setSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);

  const fetchSnapshot = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/health-monitor/status`);
      setSnapshot(data);
    } catch (e) {
      toast.error("Неуспешно зареждане на health status");
    }
  }, [API]);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/health-monitor/history?days=7`);
      setHistory(data.history || []);
    } catch (e) {
      // silent — history може да е празна
    }
  }, [API]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchSnapshot(), fetchHistory()]);
    setLoading(false);
  }, [fetchSnapshot, fetchHistory]);

  useEffect(() => {
    loadAll();
    // Auto-refresh snapshot на всеки 60 сек
    const interval = setInterval(fetchSnapshot, 60000);
    return () => clearInterval(interval);
  }, [loadAll, fetchSnapshot]);

  const handleRunNow = async () => {
    setTriggering(true);
    try {
      const { data } = await axios.post(`${API}/health-monitor/run-now`);
      toast.success(data.email_sent
        ? `✅ Репорт пуснат · email изпратен (статус: ${data.overall_status})`
        : `⚠️ Репорт пуснат · но email не тръгна (статус: ${data.overall_status})`);
      await loadAll();
    } catch (e) {
      toast.error("Неуспешен manual run: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setTriggering(false);
    }
  };

  if (loading && !snapshot) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  const overall = snapshot?.overall_status || "OK";
  const c = STATUS_COLORS[overall] || STATUS_COLORS.OK;

  return (
    <div className="space-y-6" data-testid="admin-health-dashboard">
      {/* Overall header */}
      <div
        className={`rounded-xl p-5 border-2 flex items-center justify-between gap-4`}
        style={{ background: c.bg, borderColor: c.border }}
      >
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl" style={{ background: "white" }}>
            <Activity className="w-6 h-6" style={{ color: c.fg }} />
          </div>
          <div>
            <div className="text-xs font-semibold opacity-75" style={{ color: c.fg }}>
              NUTRAMILO SYSTEM STATUS
            </div>
            <div className="text-xl font-bold" style={{ color: c.fg }}>
              {overall === "OK" && "✅ Всички системи работят нормално"}
              {overall === "WARN" && "⚠️ Има предупреждения — прегледайте"}
              {overall === "FAIL" && "🛑 Критични проблеми засечени"}
            </div>
            <div className="text-xs opacity-70 mt-0.5" style={{ color: c.fg }}>
              Последна проверка: {snapshot?.generated_at
                ? new Date(snapshot.generated_at).toLocaleString("bg-BG")
                : "—"}
              {" · "}OK {snapshot?.summary?.ok || 0} · WARN {snapshot?.summary?.warn || 0} · FAIL {snapshot?.summary?.fail || 0}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadAll}
            data-testid="health-refresh-btn"
            className="bg-white"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Обнови
          </Button>
          <Button
            size="sm"
            onClick={handleRunNow}
            disabled={triggering}
            data-testid="health-run-now-btn"
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {triggering
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <Mail className="w-3.5 h-3.5 mr-1.5" />}
            Изпрати Email сега
          </Button>
        </div>
      </div>

      {/* System cards grid */}
      <div>
        <div className={`text-sm font-semibold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
          Real-time статус на 5 системи
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(snapshot?.checks || []).map((c) => (
            <SystemCard key={c.name} check={c} isDark={isDark} />
          ))}
        </div>
      </div>

      {/* History heatmap */}
      <div>
        <div className={`text-sm font-semibold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
          Исторически trend — последни 7 дни
        </div>
        <Card className={isDark ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)]" : ""}>
          <CardContent className="p-4">
            <HistoryHeatmap history={history} isDark={isDark} />
            <div className={`mt-3 pt-3 border-t ${isDark ? "border-white/10" : "border-gray-100"} flex items-center justify-between text-[11px]`}>
              <div className={isDark ? "text-gray-500" : "text-gray-500"}>
                Heatmap показва worst статус за деня. Hover за детайли.
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ background: STATUS_COLORS.OK.bg }}></div>OK</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ background: STATUS_COLORS.WARN.bg }}></div>WARN</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ background: STATUS_COLORS.FAIL.bg }}></div>FAIL</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info footer */}
      <div className={`text-[11px] italic ${isDark ? "text-gray-500" : "text-gray-500"} text-center pt-2`}>
        Автоматичен daily report се изпраща в 06:30 UTC до HEALTH_REPORT_EMAIL. Disable с <code>DISABLE_HEALTH_MONITOR=1</code>.
      </div>
    </div>
  );
}
